<template>
  <div class="relative">
    <!-- Canvas 动画容器 -->
    <canvas
      v-if="imageLoaded"
      ref="canvasRef"
      :width="SPRITE_WIDTH"
      :height="imageHeight"
      class="canvas-container w-full image-rendering-pixelated"
    ></canvas>

    <!-- 加载状态 -->
    <div
      v-if="!imageLoaded"
      class="loading loading-ring loading-lg absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, nextTick, computed } from "vue";

const SPRITE_WIDTH = 48;

const props = defineProps({
  url: {
    type: String,
    required: true,
  },
  rate: {
    type: Number,
    default: 4,
  },
});

const emit = defineEmits<{
  (e: "loaded", totalFrames: number): void;
}>();

// 响应式变量
const imageWidth = ref(0);
const imageHeight = ref(0);
const imageLoaded = ref(false);
const totalFrames = ref(0);
const currentFrame = ref(0);
const isPlaying = ref(false);
const animationId = ref<number | null>(null);

// Canvas 相关
const canvasRef = ref<HTMLCanvasElement | null>(null);
const ctx = ref<CanvasRenderingContext2D | null>(null);
const spriteImage = ref<HTMLImageElement | null>(null);

const timeout = computed(() => {
  return 1000 / props.rate;
});

// 加载图片并获取尺寸
const loadImage = () => {
  const img = new Image();
  img.onload = () => {
    imageWidth.value = img.width;
    imageHeight.value = img.height;
    totalFrames.value = Math.floor(img.width / SPRITE_WIDTH);
    imageLoaded.value = true;
    spriteImage.value = img;

    emit("loaded", totalFrames.value);
    // 等待 DOM 更新后初始化 Canvas
    nextTick(() => {
      initCanvas();
      drawFrame();
      startAnimation();
    });
  };
  img.onerror = () => {
    console.error("图片加载失败:", props.url);
  };
  img.src = props.url;
};

// 初始化 Canvas
const initCanvas = () => {
  if (canvasRef.value) {
    ctx.value = canvasRef.value.getContext("2d");
    if (ctx.value) {
      // 设置图像渲染质量
      ctx.value.imageSmoothingEnabled = false;
    }
  }
};

// 绘制当前帧
const drawFrame = () => {
  if (!ctx.value || !spriteImage.value) return;

  // 清空画布
  ctx.value.clearRect(0, 0, SPRITE_WIDTH, imageHeight.value);

  // 绘制当前帧
  ctx.value.drawImage(
    spriteImage.value,
    currentFrame.value * SPRITE_WIDTH, // 源图片 x 坐标
    0, // 源图片 y 坐标
    SPRITE_WIDTH, // 源图片宽度
    imageHeight.value, // 源图片高度
    0, // 目标 x 坐标
    0, // 目标 y 坐标
    SPRITE_WIDTH, // 目标宽度
    imageHeight.value // 目标高度
  );
};

// 动画循环
const animate = () => {
  if (!isPlaying.value) return;

  currentFrame.value = (currentFrame.value + 1) % totalFrames.value;
  drawFrame();

  // 使用 requestAnimationFrame 替代 setInterval，性能更好
  setTimeout(() => {
    animationId.value = requestAnimationFrame(animate);
  }, timeout.value); // 控制帧率：100ms = 10fps
};

// 开始动画
const startAnimation = () => {
  if (animationId.value) {
    cancelAnimationFrame(animationId.value);
  }

  isPlaying.value = true;
  animationId.value = requestAnimationFrame(animate);
};

// 组件挂载时加载图片
onMounted(() => {
  loadImage();
});

// 组件卸载时清理动画
onUnmounted(() => {
  if (animationId.value) {
    cancelAnimationFrame(animationId.value);
  }
});
</script>

<style scoped>
.canvas-container {
  image-rendering: pixelated;
}
</style>
